import * as Sentry from "@sentry/react"
import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { ButtonGroup } from "design-system/atoms/ButtonGroup"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { cn } from "design-system/utils"
import {
  Archive,
  Hand,
  History,
  MoreHorizontal,
  PauseCircle,
  PlayCircle,
  Rocket,
  Send,
} from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useArchiveCampaignMutation } from "modules/campaigns/hooks/useArchiveCampaignMutation"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useCampaignSaveContext } from "modules/campaigns/hooks/useCampaignSave"
import { useLaunchTooltip } from "modules/campaigns/hooks/useLaunchTooltip"
import { useUnarchiveCampaignMutation } from "modules/campaigns/hooks/useUnarchiveCampaignMutation"
import { useUpdateCampaignStatusMutation } from "modules/campaigns/hooks/useUpdateCampaignStatusMutation"
import { ChangeLogItem, ChangeLogValuesByModel } from "modules/shared/components/ChangeLogItem"
import { useCallback, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CampaignDetailResponseDataSchema,
  CampaignStatusEnum,
  CampaignTriggerTypeEnum,
  ChangeLogModel,
  ChannelEnum,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { LaunchTooltip } from "../LaunchTooltip"
import { BackofficeLaunchCampaign } from "./BackofficeLaunchCampaign"
import { LaunchCampaign } from "./LaunchCampaign"

type Props = {
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
}

const CAMPAIGN_TEXT_FIELDS: Array<keyof ChangeLogValuesByModel["Campaign"]> = [
  "description",
  "toneOfVoice",
]

export function CampaignHeaderActions({ campaign }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLaunchingDialogOpen, setIsLaunchingDialogOpen] = useState(false)
  const [isChangeLogOpen, setIsChangeLogOpen] = useState(false)
  const [isBackofficeLaunchOpen, setIsBackofficeLaunchOpen] = useState(false)

  const { data: authEmployee } = useAuthEmployeeQuery()
  const isAdmin = Boolean(authEmployee?.isAdmin)

  const { mutateAsync: updateCampaignStatus, isPending: isUpdatingStatus } =
    useUpdateCampaignStatusMutation(campaign.id)
  const { mutateAsync: archiveCampaign, isPending: isArchiving } = useArchiveCampaignMutation(
    campaign.id,
  )
  const { mutateAsync: unarchiveCampaign, isPending: isUnarchiving } = useUnarchiveCampaignMutation(
    campaign.id,
  )
  const { flush } = useCampaignSaveContext()

  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()
  const { hasErrors, campaignPayload } = useLaunchTooltip(form, campaign)

  const handleLaunch = useCallback(() => {
    if (hasErrors) return
    setIsLaunchingDialogOpen(true)
  }, [hasErrors])

  const handleManualLaunch = useCallback(async () => {
    if (hasErrors) return

    await flush()
    navigate({ to: "/campaigns/$id/insights/launch", params: { id: campaign.id } })
  }, [hasErrors, flush, navigate, campaign.id])

  const handlePause = useCallback(async () => {
    if (isUpdatingStatus) return

    const prevStatus = campaign.status
    await updateCampaignStatus({
      status: CampaignStatusEnum.Paused,
      campaignPayload,
    })

    toast.success(t("campaigns.detail.pauseSuccess"), {
      action: {
        label: t("shared.undo"),
        onClick: () => updateCampaignStatus({ status: prevStatus, campaignPayload }),
      },
    })
  }, [isUpdatingStatus, campaign.status, updateCampaignStatus, campaignPayload, t])

  const handleActivate = useCallback(async () => {
    if (isUpdatingStatus || hasErrors || !campaignPayload) return

    await flush()
    const prevStatus = campaign.status
    await updateCampaignStatus({ status: CampaignStatusEnum.Active, campaignPayload })

    toast.success(t("campaigns.detail.activateSuccess"), {
      action: {
        label: t("shared.undo"),
        onClick: () => updateCampaignStatus({ status: prevStatus, campaignPayload }),
      },
    })
  }, [
    isUpdatingStatus,
    hasErrors,
    campaignPayload,
    campaign.status,
    updateCampaignStatus,
    t,
    flush,
  ])

  const handleArchive = useCallback(async () => {
    if (isArchiving) return
    await archiveCampaign()

    toast.success(t("campaigns.detail.archiveSuccess"), {
      action: {
        label: t("shared.undo"),
        onClick: () => unarchiveCampaign(),
      },
    })
  }, [isArchiving, archiveCampaign, t, unarchiveCampaign])

  const handleUnarchive = useCallback(async () => {
    if (isUnarchiving) return
    await unarchiveCampaign()

    toast.success(t("campaigns.detail.unarchiveSuccess"), {
      action: {
        label: t("shared.undo"),
        onClick: () => archiveCampaign(),
      },
    })
  }, [isUnarchiving, unarchiveCampaign, t, archiveCampaign])

  const isManualTrigger = campaign.trigger === CampaignTriggerTypeEnum.Manual

  const renderPrimaryButton = () => {
    if (campaign.archivedAt || campaign.isLogistics) return null

    if (isManualTrigger) {
      return (
        <LaunchTooltip
          showTooltip
          form={form}
          campaign={campaign}
          render={(hasErrors) => (
            <Button
              variant="outline"
              onClick={handleManualLaunch}
              aria-disabled={hasErrors}
              className={cn(hasErrors && "select-none opacity-50 cursor-default hover:bg-inherit")}
            >
              <Rocket size={14} />
              <span>{t("campaigns.options.newLaunch")}</span>
            </Button>
          )}
        />
      )
    }

    switch (campaign.status) {
      case CampaignStatusEnum.Draft:
        return (
          <LaunchTooltip
            showTooltip
            form={form}
            campaign={campaign}
            render={(hasErrors) => (
              <Button
                variant="outline"
                onClick={async () => {
                  if (hasErrors) return
                  await flush()
                  handleLaunch()
                }}
                aria-disabled={hasErrors}
                className={cn(
                  hasErrors && "select-none opacity-50 cursor-default hover:bg-inherit",
                )}
              >
                <Rocket size={14} />
                <span>{t("campaigns.options.launch")}</span>
              </Button>
            )}
          />
        )

      case CampaignStatusEnum.Paused:
        return (
          <LaunchTooltip
            showTooltip
            form={form}
            campaign={campaign}
            render={(hasErrors) => (
              <Button
                variant="outline"
                onClick={handleActivate}
                disabled={isUpdatingStatus}
                aria-disabled={hasErrors || isUpdatingStatus}
                className={cn(
                  hasErrors && "select-none opacity-50 cursor-default hover:bg-inherit",
                )}
              >
                <PlayCircle size={14} />
                <span>{t("campaigns.options.activate")}</span>
              </Button>
            )}
          />
        )

      case CampaignStatusEnum.Active:
        return (
          <Button variant="outline" onClick={handlePause} disabled={isUpdatingStatus}>
            <PauseCircle size={14} />
            <span>{t("campaigns.options.pause")}</span>
          </Button>
        )

      case CampaignStatusEnum.Launched:
      case CampaignStatusEnum.Finished:
        return null
    }
  }

  const isArchived = Boolean(campaign.archivedAt)
  const showManualLaunch =
    !isArchived &&
    !isManualTrigger &&
    campaign.status === CampaignStatusEnum.Active &&
    campaign.flow === "Outbound"
  const showChangeLog = !isArchived && Boolean(campaign.hasChangeLog)
  const showBackofficeLaunch = !isArchived && isAdmin && campaign.channel !== ChannelEnum.Api
  const showArchive =
    !isArchived &&
    [CampaignStatusEnum.Draft, CampaignStatusEnum.Finished, CampaignStatusEnum.Paused].includes(
      campaign.status,
    )
  const showUnarchive = isArchived

  const hasOverflowItems =
    showManualLaunch || showChangeLog || showBackofficeLaunch || showArchive || showUnarchive

  // NOTE: Do not try to memoize this, because it's quite complex to get all dependencies right
  const button = renderPrimaryButton()

  return (
    <>
      {isLaunchingDialogOpen && (
        <LaunchCampaign
          campaign={campaign}
          form={form}
          onClose={() => setIsLaunchingDialogOpen(false)}
        />
      )}
      {showChangeLog && (
        <ChangeLogItem
          open={isChangeLogOpen}
          onOpenChange={setIsChangeLogOpen}
          model={ChangeLogModel.Campaign}
          recordId={campaign.id.toString()}
          title={campaign.name}
          fieldsMap={[
            { field: "description", label: t("campaigns.detail.customization.brief.title") },
            { field: "toneOfVoice", label: t("campaigns.detail.customization.toneOfVoice.title") },
            { field: "nodeAdded", label: t("campaigns.changelog.nodeAdded") },
            { field: "nodeRemoved", label: t("campaigns.changelog.nodeRemoved") },
            { field: "nodeUpdated", label: t("campaigns.changelog.nodeUpdated") },
          ]}
          valueFormatter={(key, value) => {
            if (key !== "nodeAdded" && key !== "nodeRemoved" && key !== "nodeUpdated") return null
            try {
              const parsed = JSON.parse(String(value)) as {
                nodeType: string
                subtype?: string
                from?: string
                to?: string
              }
              const nodeTypeLabel = t(`campaigns.changelog.nodeType.${parsed.nodeType}`, {
                defaultValue: parsed.nodeType,
              })
              if (key === "nodeUpdated" && parsed.from && parsed.to) {
                const fromLabel = t(
                  `campaigns.changelog.nodeSubtype.${parsed.nodeType}.${parsed.from}`,
                  { defaultValue: parsed.from },
                )
                const toLabel = t(
                  `campaigns.changelog.nodeSubtype.${parsed.nodeType}.${parsed.to}`,
                  { defaultValue: parsed.to },
                )
                return t("campaigns.changelog.nodeUpdatedValue", {
                  nodeType: nodeTypeLabel,
                  from: fromLabel,
                  to: toLabel,
                })
              }
              if (parsed.subtype) {
                const subtypeLabel = t(
                  `campaigns.changelog.nodeSubtype.${parsed.nodeType}.${parsed.subtype}`,
                  { defaultValue: parsed.subtype },
                )
                return `${nodeTypeLabel}: ${subtypeLabel}`
              }
              return nodeTypeLabel
            } catch (error) {
              Sentry.captureException(error, {
                extra: { changeLogKey: key, value: String(value) },
              })
              return String(value)
            }
          }}
          canApplyChanges={(snapshot) =>
            CAMPAIGN_TEXT_FIELDS.some(
              (field) =>
                field in snapshot &&
                snapshot[field] !== (campaign as Record<string, unknown>)[field],
            )
          }
          onApplyChanges={async (snapshot) => {
            form.setValue("description", snapshot.description ?? null, { shouldDirty: true })
            form.setValue("toneOfVoice", snapshot.toneOfVoice ?? null, { shouldDirty: true })
            await flush()
          }}
        />
      )}
      {showBackofficeLaunch && (
        <BackofficeLaunchCampaign
          campaign={campaign}
          open={isBackofficeLaunchOpen}
          onOpenChange={setIsBackofficeLaunchOpen}
        />
      )}
      {(button || hasOverflowItems) && (
        <ButtonGroup>
          {button}
          {hasOverflowItems && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {showManualLaunch && (
                  <DropdownMenuItem asChild>
                    <Link to="/campaigns/$id/insights/launch" params={{ id: campaign.id }}>
                      <Hand size={16} />
                      <span>{t("campaigns.options.manualLaunch")}</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                {showChangeLog && (
                  <DropdownMenuItem onClick={() => setIsChangeLogOpen(true)}>
                    <History size={16} />
                    <span>{t("campaigns.options.changeLog")}</span>
                  </DropdownMenuItem>
                )}
                {showBackofficeLaunch && (
                  <DropdownMenuItem
                    onClick={() => setIsBackofficeLaunchOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Send size={16} />
                    <span>{t("campaigns.detail.backofficeLaunchCampaign.launch")}</span>
                  </DropdownMenuItem>
                )}
                {showArchive && (
                  <DropdownMenuItem
                    onClick={handleArchive}
                    disabled={isArchiving}
                    className="focus:text-destructive"
                  >
                    <Archive size={16} />
                    <span>{t("campaigns.options.archive")}</span>
                  </DropdownMenuItem>
                )}
                {showUnarchive && (
                  <DropdownMenuItem onClick={handleUnarchive} disabled={isUnarchiving}>
                    <Archive size={16} />
                    <span>{t("campaigns.options.unarchive")}</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </ButtonGroup>
      )}
    </>
  )
}
